import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Button, Stack, TextField, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import AlertUI from '../../generic/AlertUI'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import LoadMachineStorageInstancesDialog from './LoadMachineStorageInstancesDialog'

// Load tab of MachineStorageDialog — moves a reference from the general stock into a chosen Machine
// Storage (CU2, RAW MATERIAL). Content only: the enclosing dialog owns the modal shell and its
// accept button triggers `submit` (exposed via ref). Same domain effect as the MOS load: whole
// instances moved, `overshoot` reported when it happens.
const LoadMachineStoragePanel = forwardRef(({ open = false, reference = {}, onClose = () => {}, onSuccess = () => {}, onLoadingChange = () => {} }, ref) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert, dismiss] = AlertUI()
  const [machineStorages, setMachineStorages] = useState([])
  const [selectedMachineStorage, setSelectedMachineStorage] = useState({})
  const [quantity, setQuantity] = useState('')
  const [selectedInstanceSelections, setSelectedInstanceSelections] = useState([])
  const [instancesDialogOpen, setInstancesDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedMachineStorage({})
    setQuantity('')
    setSelectedInstanceSelections([])
    setInstancesDialogOpen(false)
    setLoading(false)
    dismiss()
    apiGet('machineStorages').then((data) => setMachineStorages(data || []))
  }, [open, dismiss])

  useEffect(() => { onLoadingChange(loading) }, [loading, onLoadingChange])

  // Instance selection overrides the quantity: when the configurator picks explicit general-stock
  // instances, those exact instances are loaded (no overshoot); otherwise the backend auto-selects
  // whole instances covering `quantity` (the pre-existing behaviour).
  const hasInstanceSelection = selectedInstanceSelections.length > 0

  const submitLoad = async () => {
    const requestedQuantity = Number(quantity)
    if (!selectedMachineStorage.machineStorageId) return
    if (!hasInstanceSelection && !(requestedQuantity > 0)) return
    // Auto-quantity for a non-fractionable reference must be a whole number; otherwise the backend
    // rejects it with 422 NON_INTEGER_QUANTITY_FOR_NON_FRACTIONABLE. Validate it up front.
    if (!hasInstanceSelection && reference.fractionable !== true && !Number.isInteger(requestedQuantity)) {
      showAlert({ severity: 'warning', title: t_('Quantity must be a whole number for this reference') })
      return
    }
    setLoading(true)
    try {
      const body = hasInstanceSelection
        ? { referenceId: reference.id, instanceSelections: selectedInstanceSelections }
        : { referenceId: reference.id, quantity: requestedQuantity }
      const result = await apiPost(
        'rawMaterials/machineStorage/' + selectedMachineStorage.machineStorageId + '/load',
        body
      )
      onSuccess()
      if (result.overshoot) {
        // Overshoot is not an error, but it is a deviation the operator must see, so the modal stays
        // open to show the warning instead of closing on it.
        const loadedQuantity = result.loadedQuantity ?? requestedQuantity
        showAlert({
          severity: 'warning',
          title: t_('Loaded more than requested'),
          message: t_(
            'Loaded {{loaded}} units instead of the {{requested}} units requested because whole instances could not cover the exact quantity',
            { loaded: loadedQuantity, requested: result.requestedQuantity ?? requestedQuantity }
          )
        })
        return
      }
      // Clean success (no error): close the modal.
      onClose()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({ submit: submitLoad }))

  return (
    <Stack spacing={2} sx={{ minWidth: '20rem' }}>
      {AlertElement}
      <Typography variant='body2' color='text.secondary'>
        {t_('Reference') + ': ' + (reference.code || '')}
      </Typography>
      <SmartSelect
        selectableOptions={machineStorages}
        value={selectedMachineStorage.machineStorageId ? selectedMachineStorage : null}
        onChange={(newValue) => setSelectedMachineStorage(newValue || {})}
        optionLabelTransform={(option) => option?.machineName || ''}
        label={t_('Destination machine storage')}
      />
      <TextField
        fullWidth
        type='number'
        value={hasInstanceSelection ? '' : quantity}
        label={t_('Quantity')}
        onChange={(event) => setQuantity(event.target.value)}
        size='small'
        inputProps={{ min: 1, step: reference.fractionable ? 'any' : 1 }}
        disabled={hasInstanceSelection}
      />
      <Stack direction='row' spacing={1} alignItems='center'>
        <Button
          variant='outlined'
          size='small'
          startIcon={<VisibilityIcon />}
          onClick={() => setInstancesDialogOpen(true)}
          disabled={!reference.id}
        >
          {t_('Select instances')}
        </Button>
        {hasInstanceSelection && (
          <Typography variant='body2' color='text.secondary'>{selectedInstanceSelections.length + ' ' + t_('items selected')}</Typography>
        )}
      </Stack>
      <LoadMachineStorageInstancesDialog
        open={instancesDialogOpen}
        referenceId={reference.id}
        referenceCode={reference.code || ''}
        selectedInstanceSelections={selectedInstanceSelections}
        onClose={() => setInstancesDialogOpen(false)}
        onConfirm={(selections) => setSelectedInstanceSelections(selections)}
      />
    </Stack>
  )
})

export default LoadMachineStoragePanel
